import React from 'react';
import { Lock, History } from 'lucide-react';
import { Modal, Button, Empty, LoadingBlock, ErrorBlock, Tip, useLoad } from '../kit';

// The time lock on an SC time entry (owner spec 26 Oct 2026). Saving is final; the time locks when
// the claim file or invoice holding the entry is downloaded or sent, or its pay batch is marked
// Paid. A locked entry keeps an editable case note, and every note edit is a new version.
// Entry fields (docs/sc-time-lock-contract.md): locked, lock_message, note_edited_at,
// note_edited_by_name, version_count.

const pad = (n) => String(n).padStart(2, '0');
// DD/MM/YYYY. A bare date (YYYY-MM-DD) is that calendar day; a timestamp is shown in local time.
export function dmy(d) {
  if (!d) return '';
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, dd] = s.split('-'); return `${dd}/${m}/${y}`; }
  const t = new Date(s);
  if (Number.isNaN(t.getTime())) return s;
  return `${pad(t.getDate())}/${pad(t.getMonth() + 1)}/${t.getFullYear()}`;
}
// DD/MM/YYYY h:mm am/pm, local time.
export function dmyTime(d) {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return String(d);
  const time = t.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  return `${dmy(d)} ${time}`;
}

// Pay statuses: saved time is "Not paid" until a bill, payroll export or paid pay batch.
const PAY_LABEL = { draft: 'Not paid', submitted: 'Not paid', approved: 'Not paid', billed: 'Billed', exported: 'Exported to payroll', paid: 'Paid' };
const PAY_TONE = { billed: 'violet', exported: 'violet', paid: 'emerald' };
export const payLabel = (status, label) => label || PAY_LABEL[status] || 'Not paid';
export const payTone = (status) => PAY_TONE[status] || 'blue';

export const LOCK_FALLBACK = 'Time can’t be changed. Use Add adjustment to correct it.';
export const lockText = (e) => (e && e.lock_message) || LOCK_FALLBACK;

// A padlock with the lock reason on hover and for screen readers.
export function LockBadge({ entry, size = 12, className = 'text-violet-600' }) {
  if (!entry || !entry.locked) return null;
  const text = lockText(entry);
  return (
    <Tip content={text}>
      <span className="inline-flex flex-shrink-0" title={text}>
        <Lock size={size} className={className} aria-hidden />
        <span className="sr-only">Locked. {text}</span>
      </span>
    </Tip>
  );
}

// "Edited 12/11/2026 by Sam Lee", with a History link when there is more than one version.
export function EditedLine({ entry, onHistory, className = '' }) {
  if (!entry || !entry.note_edited_at) return null;
  const many = Number(entry.version_count) > 1;
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-1.5 text-[12px] text-slate-500 ${className}`}>
      <span>Edited {dmy(entry.note_edited_at)}{entry.note_edited_by_name ? ` by ${entry.note_edited_by_name}` : ''}</span>
      {many && onHistory && (
        <button type="button" onClick={(ev) => { ev.stopPropagation(); onHistory(entry); }}
          className="font-semibold text-indigo-700 underline decoration-indigo-300 underline-offset-2 hover:decoration-indigo-700 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
          History
        </button>
      )}
    </span>
  );
}

// Every version of a case note, newest first, with the date, time and person.
export function NoteHistoryModal({ entry, onClose }) {
  const id = entry && entry.id;
  const h = useLoad(id ? `/sc/time-entries/${id}/history` : null);
  const versions = (h.data && h.data.versions) || [];
  return (
    <Modal open={!!entry} onClose={onClose} title="Case note history" width="lg"
      sub={entry ? 'Every version of this note, newest first. Editing a note never changes the time, claim, pay or lock.' : undefined}
      footer={<Button onClick={onClose}>Close</Button>}>
      {h.error ? <ErrorBlock error={h.error} onRetry={h.reload} /> : !h.data ? <LoadingBlock rows={3} /> : !versions.length ? (
        <Empty icon={History} title="No earlier versions">This note hasn’t been edited.</Empty>
      ) : (
        <ol className="space-y-3">
          {versions.map((v, i) => (
            <li key={v.version_no || i} className="rounded-xl ring-1 ring-inset ring-slate-200 px-4 py-3">
              <p className="flex flex-wrap items-center gap-x-2 text-[12.5px] text-slate-600">
                <span className="font-semibold text-slate-900">Version {v.version_no}{i === 0 ? ' (current)' : ''}{Number(v.version_no) === 1 ? ' (original)' : ''}</span>
                <span>{dmyTime(v.edited_at)}</span>
                {v.edited_by_name && <span>by {v.edited_by_name}</span>}
              </p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">{v.summary}</p>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
